<!--
// app/components/pages/players-player/PlayerSeasonMatches.vue
-->
<script setup lang="ts">
import type { Match } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import type { MatchStatsPlayerData, PlayerMatchStats } from "~/src/stats/domain/models/MatchStats";
import type { Player } from "~/src/player/domain/Player";
import type { Image } from "~/src/Shared/Types";
import type { UnwrapRefSimple } from "@vue/reactivity";
import type { Parameter } from "~/src/stats/domain/models/Parameter";
import type { Season, SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import { setAliasUrl } from "~/src/Shared/utils";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import matchStatsUiService from "~/src/stats/infrastructure/ui-services/matchStatsUiService";
import PlayerStatsModal from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/PlayerStatsModal.vue";
import parametersUiService from "~/src/stats/infrastructure/ui-services/parametersUiService";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

const route = useRoute();
const localePath = useLocalePath();
const { t } = useI18n();

type Props = {
  playerSeasonMatches: Match[];
  teams: Record<number, Team> | UnwrapRefSimple<Record<number, Team>>;
  playerId: number;
  playerImage: Image | undefined;
  teamInfo: Team | undefined;
  seasonTurns: Season | undefined;
  seasonId: number | undefined;
  mvpPartner?: SeasonStatsPartner;
};
const props = defineProps<Props>();

const { filterMatchPlayerStats } = matchStatsUiService();
const { getParameters } = parametersUiService();
const { currentSeason } = useCompetitionStore();

// const findTurnName = (season, phaseId, groupId, turnId) => {
//   const phase = season.phases.find(phase => phase.id === phaseId);
//   if (!phase) return undefined;
//
//   const group = phase.groups.find(group => group.id === groupId);
//   if (!group) return undefined;
//
//   const turn = group.turns.find(turn => turn.id === turnId);
//   return turn ? turn.shortName : undefined;
// };

const findTurnName = (phaseId: number, groupId: number, turnId: number): string | undefined | null => {
  if (!props.seasonTurns) return undefined;

  const phase = props.seasonTurns.phases.find(phase => phase.id === phaseId);
  if (!phase) return undefined;

  const group = phase.groups.find(group => group.id === groupId);
  if (!group) return undefined;

  const turn = group.turns.find(turn => turn.id === turnId);
  return turn ? turn.shortName : undefined;
};

const selectedPlayer = ref<Player>();
const filteredPlayerStats = ref<MatchStatsPlayerData[]>();
const parameters: Parameter[] | undefined = await getParameters();

async function getPlayerMatchStats(matchId) {
  if (matchId && typeof matchId === "number" && !$SEASONS_WITHOUT_DATA.includes(Number(props.seasonId))) {
    const { data, error } = await useFetch(
      `/api/v1/competition/matches/${matchId}/player-stats/${props.playerId}`
    );

    const matchPlayerData: PlayerMatchStats = data.value;

    if (matchPlayerData) {
      selectedPlayer.value = matchPlayerData.selectedPlayer;
      if (!!selectedPlayer.value && !!parameters && !!matchPlayerData.playerStatsData) {
        filteredPlayerStats.value = filterMatchPlayerStats(
          parameters,
          matchPlayerData.playerStatsData,
          matchPlayerData.selectedPlayer.role
        );

        document.body.classList.add("overflow-hidden");
      }
    } else if (error.value) {
      console.error(error.value);
    }
  }
}

const closePlayerModal = () => {
  selectedPlayer.value = undefined;
  document.body.classList.remove("overflow-hidden");
};
</script>

<template>
  <div class="p-4 rounded-lg bg-gray-900 lg:max-w-[332px] lg:h-fit lg:w-fit w-full order-1 lg:order-[0]">
    <div class="flex justify-between mb-2 lg:w-[300px] px-2 text-[10px] text-gray-400 uppercase">
      <p v-if="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))">
        {{ $t("general.matchday.abbreviation") }}
      </p>
      <p class="flex-1 text-center">{{ $t("general.match.word") }}</p>
    </div>

    <component
      :is="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId)) ? 'a' : 'div'"
      type="button"
      v-for="match in playerSeasonMatches"
      :class="[
        !$SEASONS_WITHOUT_DATA.includes(Number(seasonId)) ? 'match-stats-line' : 'match-stats-line-neutral',
      ]"
      @click="getPlayerMatchStats(match.id)"
    >
      <div class="flex gap-1 items-center justify-center">
        <p class="font-semibold text-sm whitespace-nowrap">
          <span v-html="findTurnName(match.phaseId, match.groupId, match.turnId) || ''"></span>
        </p>
      </div>

      <div class="flex items-center justify-center gap-1 w-full">
        <div class="flex gap-1 items-center">
          <p class="kql-player-match-teamName">{{ teams[match.participants.homeTeamId]?.shortName }}</p>
          <NuxtImg
            :src="setAliasUrl(teams[match.participants.homeTeamId]?.logo.url)"
            :alt="teams[match.participants.homeTeamId]?.completeName || ''"
            class="w-9 h-9"
            width="36"
            height="36"
          />
        </div>
        <p class="kql-player-match-penalties">{{ match.scores.homeScoreP }}</p>
        <p class="kql-player-match-score">{{ match.scores.homeScore }}</p>
        <p class="text-gray-400 text-[10px]">VS</p>
        <p class="kql-player-match-score">{{ match.scores.awayScore }}</p>
        <p class="kql-player-match-penalties">{{ match.scores.awayScoreP }}</p>
        <div class="flex gap-1 items-center">
          <NuxtImg
            :src="setAliasUrl(teams[match.participants.awayTeamId]?.logo.url)"
            :alt="teams[match.participants.awayTeamId]?.completeName || ''"
            class="w-9 h-9"
            width="36"
            height="36"
          />
          <p class="kql-player-match-teamName">{{ teams[match.participants.awayTeamId]?.shortName }}</p>
        </div>
      </div>

      <div class="arrow-right-icon rtl:rotate-180" v-if="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
          <path
            d="M14 9.98525C13.9999 10.3035 13.8772 10.6087 13.6589 10.8337L9.00174 15.6337C8.89434 15.7483 8.76586 15.8397 8.62382 15.9026C8.48177 15.9655 8.32899 15.9986 8.1744 16C8.01981 16.0013 7.86649 15.971 7.72341 15.9106C7.58032 15.8503 7.45033 15.7612 7.34101 15.6485C7.23169 15.5359 7.14524 15.4019 7.0867 15.2544C7.02816 15.1069 6.9987 14.9489 7.00004 14.7896C7.00139 14.6302 7.03351 14.4728 7.09452 14.3264C7.15554 14.18 7.24424 14.0476 7.35544 13.9369L11.1894 9.98525L7.35544 6.03365C7.14336 5.80733 7.02601 5.50421 7.02866 5.18957C7.03131 4.87494 7.15376 4.57396 7.36962 4.35147C7.58549 4.12898 7.87751 4.00278 8.18278 4.00005C8.48805 3.99731 8.78215 4.11826 9.00174 4.33685L13.6589 9.13685C13.8772 9.36185 13.9999 9.66702 14 9.98525Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </component>
  </div>

  <player-stats-modal
    v-if="selectedPlayer && filteredPlayerStats"
    :player="selectedPlayer"
    :team="teamInfo"
    :playerImage="playerImage"
    :playerStatsData="filteredPlayerStats"
    :seasonId="seasonId"
    @close="closePlayerModal"
    :mvp-partner="mvpPartner"
  ></player-stats-modal>
</template>

<style scoped>
.kql-player-match-teamName {
  @apply text-sm lg:text-base;
}

.kql-player-match-penalties {
  @apply font-bold text-xs text-primary;
}

.kql-player-match-score {
  @apply font-bold text-sm lg:text-base;
}

.match-stats-line,
.match-stats-line-neutral {
  @apply rounded bg-gray-800 px-2 flex items-center justify-between mb-2 py-2;
}

.arrow-right-icon {
  @apply text-gray-600 transition-all duration-200 ease-in-out;
}

.arrow-right-icon svg {
  @apply w-5 h-[18px];
}

.match-stats-line:hover .arrow-right-icon {
  @apply text-secondary ms-2;
}
</style>
